import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { LuDices, LuCornerUpLeft, LuRotateCcw, LuDelete, LuCheck, LuGrid2X2 } from 'react-icons/lu';

import { IClient } from '~/types/client';
import { logEvent } from '~/config/firebase.config';
import { BingoAPI, BINGO_ROOM_ID, ALLOWED_MAX, DEFAULT_MAX, type BingoState } from '~/services/db/bingo';

import styles from './Animator.module.css';

/**
 * Animator Control — pavé de saisie du Disco Bingo. Surface PRINCIPALE de saisie
 * (téléphone de l'animateur, authentifié). Écrit dans le doc durable via le proxy
 * `BingoAPI` (la console est l'autorité Firestore, gotcha #75) et s'abonne en
 * temps réel pour refléter l'état (numéro courant, déjà tirés, plage).
 */
export function BingoKeypadPanel({ client }: { client: IClient }) {
    const { t } = useTranslation();
    const clientID = client.id;
    const [state, setState] = useState<BingoState>({ drawn: [], maxNumber: DEFAULT_MAX });
    const [typed, setTyped] = useState('');
    const [msg, setMsg] = useState('');
    const [resetArmed, setResetArmed] = useState(false);
    const resetTimer = useRef<number | null>(null);
    const mounted = useRef(true);

    useEffect(() => {
        const unsub = BingoAPI.subscribe(clientID, BINGO_ROOM_ID)(setState);
        return () => unsub();
    }, [clientID]);

    useEffect(
        () => () => {
            mounted.current = false;
            if (resetTimer.current) window.clearTimeout(resetTimer.current);
        },
        []
    );

    const { drawn, maxNumber } = state;
    const current = drawn.length ? drawn[drawn.length - 1] : null;

    const flash = (text: string) => {
        setMsg(text);
        window.setTimeout(() => {
            if (mounted.current) setMsg((prev) => (prev === text ? '' : prev));
        }, 2500);
    };

    const draw = (n: number) => {
        if (!Number.isFinite(n) || n < 1 || n > maxNumber) {
            flash(t('animator.bingo.outOfRange', { max: maxNumber }));
            return;
        }
        if (drawn.includes(n)) {
            flash(t('animator.bingo.alreadyDrawn', { number: n }));
            return;
        }
        logEvent('animator_bingo_draw', clientID, { clientID, number: n });
        BingoAPI.draw(clientID, BINGO_ROOM_ID, n).catch(() => flash(t('animator.bingo.drawFailed')));
    };

    const commit = () => {
        if (!typed) return;
        draw(parseInt(typed, 10));
        setTyped('');
    };

    const pushDigit = (d: string) => setTyped((prev) => (prev.length >= 2 ? d : prev + d));

    const onRandom = () => {
        const avail: number[] = [];
        for (let n = 1; n <= maxNumber; n += 1) if (!drawn.includes(n)) avail.push(n);
        if (!avail.length) {
            flash(t('animator.bingo.allDrawn'));
            return;
        }
        draw(avail[Math.floor(Math.random() * avail.length)]);
    };

    const onUndo = () => {
        if (!drawn.length) {
            flash(t('animator.bingo.nothingToUndo'));
            return;
        }
        BingoAPI.undo(clientID, BINGO_ROOM_ID).catch(() => flash(t('animator.bingo.failedRetry')));
    };

    const onReset = () => {
        if (!resetArmed) {
            setResetArmed(true);
            resetTimer.current = window.setTimeout(() => setResetArmed(false), 3000);
            return;
        }
        if (resetTimer.current) window.clearTimeout(resetTimer.current);
        setResetArmed(false);
        logEvent('animator_bingo_reset', clientID, { clientID });
        BingoAPI.reset(clientID, BINGO_ROOM_ID).catch(() => flash(t('animator.bingo.failedRetry')));
    };

    const onSetMax = (max: number) => {
        if (max === maxNumber) return;
        BingoAPI.setMax(clientID, BINGO_ROOM_ID, max).catch(() => flash(t('animator.bingo.failedRetry')));
    };

    const keypadStyle: CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        marginTop: 8,
    };

    return (
        <div className={`${styles.btPanel} ${styles.reveal} ${styles.d3}`}>
            <div className={styles.btHead}>
                <span className={styles.btHeadIcon}>
                    <LuGrid2X2 />
                </span>
                {t('animator.bingo.title')}
            </div>

            <span className={styles.btLabel}>
                {current !== null
                    ? t('animator.bingo.current', { number: current, drawn: drawn.length, max: maxNumber })
                    : t('animator.bingo.none', { max: maxNumber })}
            </span>

            <div
                style={{
                    fontSize: 30,
                    fontWeight: 700,
                    textAlign: 'center',
                    padding: '6px 0',
                    letterSpacing: 1,
                }}
            >
                {typed || '—'}
            </div>

            <div style={keypadStyle}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <button key={d} type="button" className={styles.btChip} onClick={() => pushDigit(d)}>
                        {d}
                    </button>
                ))}
                <button
                    type="button"
                    className={styles.btChip}
                    aria-label={t('animator.bingo.clear')}
                    onClick={() => setTyped((prev) => prev.slice(0, -1))}
                >
                    <LuDelete />
                </button>
                <button type="button" className={styles.btChip} onClick={() => pushDigit('0')}>
                    0
                </button>
                <button
                    type="button"
                    className={`${styles.btChip} ${styles.btChipOn}`}
                    aria-label={t('animator.bingo.validate')}
                    onClick={commit}
                >
                    <LuCheck />
                </button>
            </div>

            <span className={styles.btLabel} style={{ marginTop: 10 }}>
                {t('animator.bingo.range')}
            </span>
            <div className={styles.btChips}>
                {ALLOWED_MAX.map((r) => (
                    <button
                        key={r}
                        type="button"
                        className={`${styles.btChip} ${maxNumber === r ? styles.btChipOn : ''}`}
                        onClick={() => onSetMax(r)}
                    >
                        1 – {r}
                    </button>
                ))}
            </div>

            <div className={styles.btChips} style={{ marginTop: 10 }}>
                <button type="button" className={styles.btChip} onClick={onRandom}>
                    <LuDices /> {t('animator.bingo.random')}
                </button>
                <button type="button" className={styles.btChip} onClick={onUndo}>
                    <LuCornerUpLeft /> {t('animator.bingo.undo')}
                </button>
                <button
                    type="button"
                    className={styles.btChip}
                    onClick={onReset}
                    style={resetArmed ? { borderColor: '#e24b4a', color: '#f2a88e' } : undefined}
                >
                    <LuRotateCcw /> {resetArmed ? t('animator.bingo.resetConfirm') : t('animator.bingo.reset')}
                </button>
            </div>

            {msg && (
                <span className={styles.btLabel} style={{ marginTop: 8, color: '#f2a88e' }}>
                    {msg}
                </span>
            )}
        </div>
    );
}
